import { Injectable } from '@angular/core';
import { stringifyItemId } from 'products-data';
import { CartEntity } from '../types/CartEntity';
import { CartEntityItem } from '../types/CartEntityItem';

interface UnsavedQuantity {
  item: CartEntityItem;
  /** When the API confirmed this quantity; unset while it's still on its way. */
  confirmedAt?: number;
}

/**
 * Quantities the user has set in a cart, kept until a cart load is known to include them.
 * A cart load overlays them, so a reload (cache expiry, punchout) that answers with an older
 * quantity can't undo what the user entered.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedCartQuantities {
  private readonly _carts = new Map<string, Map<string, UnsavedQuantity>>();

  public set(cId: string, item: CartEntityItem): void {
    let cart = this._carts.get(cId);
    if (!cart) {
      cart = new Map();
      this._carts.set(cId, cart);
    }
    cart.set(stringifyItemId(item.id), { item });
  }

  /** True when `quantity` is the latest one the user set for the item (or nothing is tracked for it). */
  public isLatest(cId: string, itemIdStr: string, quantity: number): boolean {
    const entry = this._carts.get(cId)?.get(itemIdStr);
    return !entry || entry.item.quantity === quantity;
  }

  /** Marks a saved quantity as confirmed. Returns false when the user has set a newer quantity since. */
  public confirm(cId: string, itemIdStr: string, quantity: number): boolean {
    if (!this.isLatest(cId, itemIdStr, quantity)) {
      return false;
    }
    const entry = this._carts.get(cId)?.get(itemIdStr);
    if (entry) {
      entry.confirmedAt = Date.now();
    }
    return true;
  }

  public delete(cId: string, itemIdStr: string): void {
    this._carts.get(cId)?.delete(itemIdStr);
  }

  public clear(cId: string): void {
    this._carts.delete(cId);
  }

  /**
   * Applies the user's quantities to a cart loaded from the API. Quantities confirmed before the
   * load started are already in it and are dropped; the rest win over what the API returned.
   */
  public applyTo(cId: string, cart: CartEntity, loadStartedAt: number): CartEntity {
    const unsaved = this._carts.get(cId);
    if (!unsaved?.size) {
      return cart;
    }
    let items = cart.items;
    for (const [itemIdStr, { item, confirmedAt }] of unsaved) {
      if (confirmedAt !== undefined && confirmedAt < loadStartedAt) {
        unsaved.delete(itemIdStr);
        continue;
      }
      const index = items.findIndex((i) => i.id.equals(item.id));
      if (index !== -1 && item.quantity === 0 && confirmedAt !== undefined) {
        // Removal already saved; the load just predates it.
        items = items.filter((_, n) => n !== index);
      } else if (index !== -1) {
        items = items.map((i, n) => (n === index ? { ...i, quantity: item.quantity } : i));
      } else if (item.quantity > 0) {
        items = [...items, item];
      }
    }
    return { ...cart, items };
  }
}
